import React, { lazy, memo, Suspense, useEffect, useMemo } from 'react';
import {
  Routes,
  Route,
  Navigate,
  unstable_HistoryRouter as HistoryRouter,
} from 'react-router-dom';
import { MainContainer } from './App.styles';
import { createBrowserHistory } from 'history';
import Charts from './pages/Charts';
import { useLoadScript } from '@react-google-maps/api';
import { allLibraries } from './constants/librariesForGoogleMaps';
import 'react-datepicker/dist/react-datepicker.css';
import localStorageKeys from './constants/localStorageKeys';
import api from './api/base';
import { userData } from './api/paths';
import { useDispatch, useSelector } from 'react-redux';
import { updateMapsStatus, updateUser } from './redux/slices/userProfile';
import { RootState } from './redux/store';
import {
  adminPermission,
  chartsPermission,
  rtvPermission,
  searchPermission,
} from './constants/userPermissions';
import PrivateRoute from './pages/PrivateRoute';
import useNotifier from './hooks/useNotifier';
import { Box } from '@mui/system';
import { CircularProgress } from '@mui/material';
import ActivateUser from './pages/ActivateUser';

import Login from './pages/Login';
import SysSecurityTerms from './pages/SysSecurityTerms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import ForgotPassword from './pages/ForgotPassword';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import Subscriptions from './pages/Subscriptions';
import NewVersionDialog from './components/NewVersionDialog';

const Profile = lazy(() => import('./pages/Profile'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const RealTimeView = lazy(() => import('./pages/RealTimeView'));
const Configuration = lazy(() => import('./pages/Configuration'));

export const history = createBrowserHistory();

history.listen(({ location }) => {
  if (typeof window !== 'undefined') {
    (window as any)?.gtag(
      'config',
      process.env.REACT_APP_GOOGLE_ANALYTICS_TAG,
      {
        page_path: location.pathname,
      },
    );
  }
});

const App = () => {
  useNotifier();
  const dispatch = useDispatch();
  const { isLoaded } = useLoadScript({
    id: 'vipr-google-maps',
    googleMapsApiKey: process.env.REACT_APP_MAPS_API as string,
    libraries: allLibraries,
  });

  const permissions = useSelector(
    (store: RootState) => store.user?.user?.permissions,
  );

  const getUserInfo = async () => {
    const { data } = await api.get(userData);
    dispatch(updateUser(data));
  };

  useEffect(() => {
    if (isLoaded) dispatch(updateMapsStatus(isLoaded));
  }, [isLoaded]);

  useEffect(() => {
    const access = localStorage.getItem(localStorageKeys.viprToken);

    if (access) {
      api.defaults.headers.common.Authorization = `Bearer ${access}`;
      getUserInfo();
    }
  }, []);

  // In order for us to allow user to user URLs for specific pages
  // We need to know if he has permission locally saved for him
  const savedPermissions: string[] = useMemo(() => {
    const perms = localStorage.getItem(localStorageKeys.permissions);
    return JSON.parse(perms || '[]');
  }, []);

  // After we fetch info from BE we will redirect user
  // Or he will just stay on the same page
  const currentPermissions
    = permissions?.map((perm) => perm.action) || savedPermissions;

  const getDefaultRouteComponent = (permissionsList?: any[]) => {
    if (permissionsList?.length) {
      let path = '/dashboard';
      if (
        permissionsList.some(
          (permission) => permission.action === searchPermission,
        )
      ) {
        path = '/dashboard';
      } else if (
        permissionsList.some(
          (permission) => permission.action === rtvPermission,
        )
      ) {
        path = '/real-time-view';
      } else if (
        permissionsList.some(
          (permission) => permission.action === chartsPermission,
        )
      ) {
        path = '/charts';
      }
      return <Navigate to={path} />;
    }
    return <Login />;
  };

  return (
    <HistoryRouter history={history}>
      <MainContainer>
        <NewVersionDialog />
        <Suspense
          fallback={
            <Box
              width="100%"
              height="100vh"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            <Route
              path="/security-terms"
              element={
                <PrivateRoute>
                  <SysSecurityTerms />
                </PrivateRoute>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <PrivateRoute>
                  <PrivacyPolicy />
                </PrivateRoute>
              }
            />
            <Route
              path="/terms-of-use"
              element={
                <PrivateRoute>
                  <TermsOfUse />
                </PrivateRoute>
              }
            />
            {(currentPermissions?.includes(searchPermission)
              || currentPermissions?.includes(adminPermission)) && (
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
            )}
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            {(currentPermissions?.includes(rtvPermission)
              || currentPermissions?.includes(adminPermission)) && (
              <Route
                path="/real-time-view"
                element={
                  <PrivateRoute>
                    <RealTimeView />
                  </PrivateRoute>
                }
              />
            )}
            {(currentPermissions?.includes(chartsPermission)
              || currentPermissions?.includes(adminPermission)) && (
              <Route
                path="/charts"
                element={
                  <PrivateRoute>
                    <Charts />
                  </PrivateRoute>
                }
              />
            )}
            {currentPermissions?.includes(adminPermission) && (
              <Route
                path="/configuration"
                element={
                  <PrivateRoute>
                    <Configuration />
                  </PrivateRoute>
                }
              />
            )}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/reset-password-confirm"
              element={<ResetPasswordConfirm />}
            />
            <Route path="/activate-user" element={<ActivateUser />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/" element={getDefaultRouteComponent(permissions)} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </MainContainer>
    </HistoryRouter>
  );
};

export default memo(App);
